import type { SuspenseQueryHookOptions } from "@apollo/client";
import { skipToken } from "@apollo/client";
import { useMutationHandler } from "@sinch/hooks/useMutationHandler";
import { useSuspenseQueryHandler } from "@sinch/hooks/useSuspenseQueryHandler";
import {
  AddRateComponent,
  ArchiveRateComponent,
  DeleteRateComponent,
  EditRateComponent,
  RateComponent,
  RateComponentList,
} from "@sinch/components/application/RateComponent/queries.graphql";
import type {
  AddRateComponentMutation,
  AddRateComponentMutationVariables,
  ArchiveRateComponentMutation,
  ArchiveRateComponentMutationVariables,
  DeleteRateComponentMutation,
  DeleteRateComponentMutationVariables,
  EditRateComponentMutation,
  EditRateComponentMutationVariables,
  RateComponentListQuery,
  RateComponentListQueryVariables,
  RateComponentQuery,
  RateComponentQueryVariables,
} from "@sinch/components/application/RateComponent/queries.types";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";

type UseRateComponentParams<TId extends string | undefined | null = string | undefined | null> = {
  id?: TId;
  queryOptions?: Omit<SuspenseQueryHookOptions<RateComponentQuery, RateComponentQueryVariables>, "variables">;
};

export const useRateComponent = <TId extends string | undefined | null = string | undefined | null>(
  props?: UseRateComponentParams<TId>
) => {
  const [archiveComponent, { loading: archiveLoading }] = useMutationHandler<
    ArchiveRateComponentMutation,
    ArchiveRateComponentMutationVariables
  >(ArchiveRateComponent);

  const [deleteComponent, { loading: deleteLoading }] = useMutationHandler<
    DeleteRateComponentMutation,
    DeleteRateComponentMutationVariables
  >(DeleteRateComponent);

  const [createComponent, { loading: createLoading }] = useMutationHandler<
    AddRateComponentMutation,
    AddRateComponentMutationVariables
  >(AddRateComponent);

  const [editComponent, { loading: editLoading }] = useMutationHandler<
    EditRateComponentMutation,
    EditRateComponentMutationVariables
  >(EditRateComponent);

  const componentId = props?.id;
  const queryVariables = componentId ? { id: componentId } : undefined;

  const { data } = useSuspenseQueryHandler<RateComponentQuery, RateComponentQueryVariables>(
    RateComponent,
    queryVariables,
    componentId ? (props.queryOptions ?? {}) : skipToken
  );

  const { data: list } = useQueryHandler<RateComponentListQuery, RateComponentListQueryVariables>(
    RateComponentList,
    undefined,
    { skip: !!componentId, ...(props?.queryOptions ?? {}) }
  );

  const getLabel = (componentId: TId extends string ? never : string | null | undefined) => {
    if (!componentId) {
      return null;
    }
    if (props?.id) {
      return data?.rateComponent?.name ?? null;
    }
    return list?.rateComponents?.find(({ id }) => id === componentId)?.name ?? null;
  };

  return {
    archive: archiveComponent,
    create: createComponent,
    edit: editComponent,
    remove: deleteComponent,
    loading: deleteLoading || editLoading || createLoading || archiveLoading,
    rateComponent: data,
    getLabel,
  };
};
